<template>
  <div id="apikey">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="apikey-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <CodeIcon :color="iconColor" class="apikey-icon" />
              {{ $t('api.title') }}
            </h1>
          </div>
          <div class="mb-7 mt-12">
            <v-btn
              color="rgba(165, 216, 103, 1)"
              class="elevation-0 font-weight-bold app-btn"
              :disabled="handleDisabled"
              :loading="loading"
              @click="createNewApiKey"
            >
              {{ $t('api.createButton') }}
              <template #loader>
                <span class="custom-loader">
                  <v-icon :icon="'cached'" />
                  {{ $t('api.creating') }}
                </span>
              </template>
            </v-btn>
          </div>
          <v-data-table
            :headers="headers"
            fixed-header
            :loading="isloading"
            :loading-text="$t('api.loading.apiKey')"
            :no-data-text="$t('api.noData.apiKey')"
            :items="apiKey"
            hide-default-footer
            item-key="key"
            class="elevation-1 users-table"
          >
            <template #progress>
              <v-progress-linear
                color="#592892"
                indeterminate
              ></v-progress-linear>
            </template>

            <template
              v-if="
                isAuthenticated &&
                checkAuthorization(['superAdmin', 'admin'])
              "
              #[`item.revoke`]="{ item }"
            >
              <div class="action-icons">
                <v-icon
                  v-if="!isHelpDesk()"
                  @click.native="deleteItem(item, 'apikeys')"
                  :icon="'delete_icon'"
                />
              </div>
            </template>
          </v-data-table>
          <div v-if="apiKey.length > 0" class="mb-7 mt-12">
            <v-data-table
              :headers="endpointHeaders"
              fixed-header
              :loading="isloading"
              :loading-text="$t('api.loading.endpoints')"
              :no-data-text="$t('api.noData.endpoints')"
              :items="indexedItems"
              hide-default-footer
              item-key="location"
              class="elevation-1 users-table"
            >
              <template #progress>
                <v-progress-linear
                  color="#592892"
                  indeterminate
                ></v-progress-linear>
              </template>

              <template #[`item.location`]="{ item }">
                <div class="my-2">{{ item.location }}</div>
                <v-text-field
                  v-if="item.param"
                  v-model="id"
                  placeholder="Enter the user's ID"
                  single-line
                  hide-details
                  density="compact"
                  :color="$vuetify.theme.dark ? '#A0C548' : '#592892'"
                  class="mb-3"
                ></v-text-field>
              </template>

              <template
                v-if="
                  isAuthenticated && checkAuthorization(['superAdmin', 'admin'])
                "
                #[`item.test`]="{ item }"
              >
                <div class="action-icons">
                  <v-btn
                    class="text-white font-weight-bold text-subtitle-2 mx-auto my-3"
                    size="small"
                    variant="flat"
                    color="#A5D867"
                    :loading="loading2 && item.id == index"
                    @click="
                      () => {
                        testEndPoint(item)
                        index = item.id
                      }
                    "
                  >
                    {{ $t('api.buttons.getPrefix') }} {{ item.title }}
                    <template #loader>
                      <div class="d-flex align-center">
                        <v-progress-circular
                          indeterminate
                          size="18"
                          width="2"
                          color="white"
                          class="mr-2"
                        />
                        <span>{{ $t('api.creating') }}</span>
                      </div>
                    </template>
                  </v-btn>
                  <v-btn
                    class="text-white font-weight-bold text-subtitle-2 mx-auto ml-3"
                    size="small"
                    variant="flat"
                    color="#383935"
                    @click="showCode(item)"
                  >
                    {{ $t('api.buttons.code') }}
                  </v-btn>
                </div>
              </template>
            </v-data-table>
            <v-data-table
              :headers="endpointHeaders"
              fixed-header
              :loading="isloading"
              :loading-text="$t('api.loading.endpoints')"
              :no-data-text="$t('api.noData.endpoints')"
              :items="indexedUserEndpoints"
              hide-default-footer
              item-key="location"
              class="elevation-1 users-table mt-12"
            >
              <template #progress>
                <v-progress-linear
                  color="#592892"
                  indeterminate
                ></v-progress-linear>
              </template>

              <template
                v-if="
                  isAuthenticated && checkAuthorization(['superAdmin', 'admin'])
                "
                #[`item.test`]="{ item }"
              >
                <div class="action-icons">
                  <v-btn
                    class="text-white font-weight-bold text-subtitle-2 mx-auto my-3"
                    size="small"
                    variant="flat"
                    color="#A5D867"
                    :loading="loading3 && item.id == index"
                    @click="
                      () => {
                        getInstructions(item)
                        index = item.id
                      }
                    "
                  >
                    {{ $t('api.buttons.getInstructions') }}
                    <template #loader>
                      <div class="d-flex align-center">
                        <v-progress-circular
                          indeterminate
                          size="18"
                          width="2"
                          color="white"
                          class="mr-2"
                        />
                        <span>{{ $t('api.creating') }}</span>
                      </div>
                    </template>
                  </v-btn>
                  <v-btn
                    class="text-white font-weight-bold text-subtitle-2 mx-auto ml-3"
                    size="small"
                    variant="flat"
                    color="#383935"
                    @click="showUserCode(item)"
                  >
                    {{ $t('api.buttons.code') }}
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </div>
          <v-btn
            v-if="
              Array.isArray(response)
                ? response.length
                : !!response || createUserResponse
            "
            class="text-white font-weight-bold text-subtitle-2 mx-auto mb-5"
            variant="flat"
            color="#383935"
            @click="
              () => {
                response = ''
                createUserResponse = ''
                showAllResults = false
              }
            "
          >
            {{ $t('api.buttons.clearResponse') }}
          </v-btn>
          <JsonViewer
            v-if="
              limitedResponse &&
              (Array.isArray(limitedResponse)
                ? limitedResponse.length
                : Object.keys(limitedResponse).length)
            "
            :value="limitedResponse"
            :expanded="true"
            :expand-depth="Infinity"
            :copyable="true"
            theme="dark"
            class="rounded"
            style="max-height: 600px; overflow: auto"
          />
          <div v-if="Array.isArray(response) && response.length" class="my-4">
            <v-btn
              v-if="!showAllResults && response.length > responseLimit"
              @click="showAllResults = true"
              color="primary"
              class="mr-2"
            >
              Show All Results ({{ response.length }})
            </v-btn>

            <v-btn @click="downloadResponseAsJson" color="secondary">
              Download JSON
            </v-btn>
          </div>

          <div v-if="createUserResponse" class="bg-white show-sample-response">
            <h2>{{ $t('api.sections.getToken.title') }}</h2>
            <p>
              {{ $t('api.sections.getToken.description') }}
            </p>
            <v-data-table
              :headers="sampleParamsHeaders"
              fixed-header
              :loading="isloading"
              :loading-text="$t('api.loading.tokenParams')"
              :no-data-text="$t('api.noData.tokenParams')"
              :items="getTokenParams"
              hide-default-footer
              class="elevation-1 mt-5 mb-5"
            >
              <template #progress>
                <v-progress-linear
                  color="#592892"
                  indeterminate
                ></v-progress-linear>
              </template>

              <template
                v-if="
                  isAuthenticated && checkAuthorization(['superAdmin', 'admin'])
                "
                #[`item.test`]="{ item }"
              >
                <div class="action-icons">
                  <v-btn
                    class="text-white font-weight-bold text-subtitle-2 mx-auto my-3"
                    size="small"
                    variant="flat"
                    color="#A5D867"
                    :loading="loading3 && item.id == index"
                    @click="
                      () => {
                        getInstructions(item)
                        index = item.id
                      }
                    "
                  >
                    {{ $t('api.buttons.getInstructions') }}
                    <template #loader>
                      <div class="d-flex align-center">
                        <v-progress-circular
                          indeterminate
                          size="18"
                          width="2"
                          color="white"
                          class="mr-2"
                        />
                        <span>{{ $t('api.creating') }}</span>
                      </div>
                    </template>
                  </v-btn>
                  <v-btn
                    class="text-white font-weight-bold text-subtitle-2 mx-auto ml-3"
                    size="small"
                    variant="flat"
                    color="#383935"
                    @click="showUserCode(item)"
                  >
                    {{ $t('api.buttons.code') }}
                  </v-btn>
                </div>
              </template>
            </v-data-table>
            <p>{{ $t('api.sections.getToken.responseNote') }}</p>
            <!-- <p>
                        "status": 200,<br>
                        "message": "Token created 2.", (2 indicates a new user was created. 1 indicates the user already exists)<br>
                        "cid": "ABCD",<br>
                        "token": "eyJhbGciOiJSUzr1NiIsInR5cCI6IkpXVCJ9.GyJhdWQiOiJodHRwczovL2lkZW50aXR5dG9vbGtpdC5nb29nbGVhcGlzLmNvbS7nb29nbGUuaWRlbnRpdHkuaWRlbnRpdHl0b29sa2l0LnYxLklkZW50aXR5VG9vbGtpdCIsImlhdCI6MTYyMzY4NTgwNSwiZXhwIjoxNjKzNjg5NDA1LCJpc3MiOiJ0aGUtam91cm5leS1wZXJpbWV0ZXJAYXBwc3BvdC5nc2VydmljZWFjY291bnQuY29tIiwic3ViIjoidGhlLWpvdXJuZXktcGVyaW1ldGVyQGFwcHNwb3QuZ3NlcnZpY2VhY2NvdW50LmNvbSIsInVpZCI6InAyVTUxcXJaMlNObXBzM05SWHFiYmxId0dUNTIifQ.mBNsS_5tOVUz_I20LW2b8Z55qfeGP0I93x598_tudcaizMTeiQckt09Vx7PF3FtfhSVLJt0cHTaub8fzU0MWVY-0xKpGtH-TfQAU1NiVTL4MAY8CwgQIQLebBwJ-F4J0mRJwLps6OfaX-s5TI7v0syP49WgpPlZDGYO45gYFQEOlAReN12uG3oZS-gIpXFEufSck-8E1C8_yY2z59lesa7WFZO7OFTVrxV5nDb2dWKl-7025lrZqAzET5v5gjXojTOwfBodlio9l2cShCnde4h6lzk0i_m0oad4FZCpQS2u9r5NmVRos6KrTiymUAx8gPu1c-kdWz38rgJAJih6RCQ",
                    </p> -->
            <v-data-table
              :headers="tokenHeaders"
              fixed-header
              :loading="isloading"
              :loading-text="$t('api.loading.tokenParams')"
              :no-data-text="$t('api.noData.tokenParams')"
              :items="tokenSampleParams"
              hide-default-footer
              class="elevation-1 mt-5 mb-5"
            >
              <template #progress>
                <v-progress-linear
                  color="#592892"
                  indeterminate
                ></v-progress-linear>
              </template>

              <template
                v-if="
                  isAuthenticated && checkAuthorization(['superAdmin', 'admin'])
                "
                #[`item.test`]="{ item }"
              >
                <div class="action-icons">
                  <v-btn
                    class="text-white font-weight-bold text-subtitle-2 mx-auto my-3"
                    size="small"
                    variant="flat"
                    color="#A5D867"
                    :loading="loading3 && item.id == index"
                    @click="
                      () => {
                        getInstructions(item)
                        index = item.id
                      }
                    "
                  >
                    {{ $t('api.buttons.getInstructions') }}
                    <template #loader>
                      <div class="d-flex align-center">
                        <v-progress-circular
                          indeterminate
                          size="18"
                          width="2"
                          color="white"
                          class="mr-2"
                        />
                        <span>{{ $t('api.creating') }}</span>
                      </div>
                    </template>
                  </v-btn>
                  <v-btn
                    class="text-white font-weight-bold text-subtitle-2 mx-auto ml-3"
                    size="small"
                    variant="flat"
                    color="#383935"
                    @click="showUserCode(item)"
                  >
                    {{ $t('api.buttons.code') }}
                  </v-btn>
                </div>
              </template>
            </v-data-table>
            <h2>{{ $t('api.sections.login.title') }}</h2>
            <p class="mt-3 mb-1">
              {{ $t('api.sections.login.step1') }}
            </p>
            <p class="text-body-2 mb-2">
              https://the-journey-perimeter.web.app/login?mode=signInWithToken&cid=ABCD&active=yes&token=eyJhbGciOiJSUzr1NiIsInR5cCI6IkpXVCJ9...
            </p>
            <p class="text-body-2 text-red">
              {{ $t('api.sections.login.note') }}
            </p>
            <p class="mt-3 mb-1">
              {{ $t('api.sections.login.step2') }}
            </p>
            <p class="text-body-2">
              https://the-journey-perimeter.web.app/login?mode=signInWithToken&cid=ABCD&token=eyJhbGciOiJSUzr1NiIsInR5cCI6IkpXVCJ9...
            </p>
            <v-data-table
              :headers="sampleParamsHeaders"
              fixed-header
              :loading="isloading"
              :loading-text="$t('api.loading.loginParams')"
              :no-data-text="$t('api.noData.loginParams')"
              :items="sampleParams"
              hide-default-footer
              class="elevation-1 mt-5"
            >
              <template #progress>
                <v-progress-linear
                  color="#592892"
                  indeterminate
                ></v-progress-linear>
              </template>

              <template
                v-if="
                  isAuthenticated && checkAuthorization(['superAdmin', 'admin'])
                "
                #[`item.test`]="{ item }"
              >
                <div class="action-icons">
                  <v-btn
                    class="text-white font-weight-bold text-subtitle-2 mx-auto my-3"
                    size="small"
                    variant="flat"
                    color="#A5D867"
                    :loading="loading3 && item.id == index"
                    @click="
                      () => {
                        getInstructions(item)
                        index = item.id
                      }
                    "
                  >
                    {{ $t('api.buttons.getInstructions') }}
                    <template #loader>
                      <div class="d-flex align-center">
                        <v-progress-circular
                          indeterminate
                          size="18"
                          width="2"
                          color="white"
                          class="mr-2"
                        />
                        <span>{{ $t('api.creating') }}</span>
                      </div>
                    </template>
                  </v-btn>
                  <v-btn
                    class="text-white font-weight-bold text-subtitle-2 mx-auto ml-3"
                    size="small"
                    variant="flat"
                    color="#383935"
                    @click="showUserCode(item)"
                  >
                    {{ $t('api.buttons.code') }}
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </div>
          <span v-if="code">
            <div class="mb-2 pl-1">
              <h3>{{ $t('api.sections.codeSnippets.title') }}</h3>
            </div>
            <CodeSnippets
              :request="request"
              :key="componentKey"
              :location="location"
              :auth="auth"
            />
          </span>
        </div>
      </v-flex>
      <v-snackbar
        v-model="snackbar"
        color="#592892"
        :timeout="timeout"
        location="top"
        class=""
      >
        <span v-html="snackbarMsg"></span>

        <template #actions="{ attrs }">
          <v-btn
            variant="text"
            v-bind="attrs"
            @click="snackbar = false"
            class="font-weight-bold"
          >
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
  // import firebase from 'firebase'
  import db from '@/firebase/init'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { mapGetters } from 'vuex'
  import axios from 'axios'
  import { functionUrl } from '@/helpers/firebaseEnv'
  import CodeSnippets from '@/components/utilities/CodeSnippets'
  import { confirmAction } from '@/components/utilities/SwalService'
  import Swal from 'sweetalert2'
  import CodeIcon from '@/assets/icons/CodeIcon.vue'
  import { toRaw } from 'vue'

  export default {
    name: 'ApiKey',
    data() {
      return {
        snackbar: false,
        snackbarMsg: '',
        componentKey: 0,
        request: '',
        timeout: 6000,
        isloading: true,
        loader: null,
        loading: false,
        loading2: false,
        loading3: false,
        id: null,
        index: -1,
        apiKey: [],
        response: [],
        createUserResponse: false,
        location: '',
        auth: '',
        code: '',
        responseLimit: 100,
        showAllResults: false,
        endpoints: [
          {
            title: 'All Users',
            request: 'GET',
            endpoint: 'users',
            location:
              functionUrl('users'),
          },
          {
            title: 'All Groups',
            request: 'GET',
            endpoint: 'groups',
            location:
              functionUrl('groups'),
          },
          {
            title: 'Specific User',
            request: 'GET',
            endpoint: 'user',
            location:
              functionUrl('user/{id}'),
            param: true,
          },
        ],
        createUserEndpoints: [
          {
            title: 'Create User',
            request: 'POST',
            endpoint: 'createUser',
            location:
              functionUrl('createUser'),
          },
        ],
        // sampleUserApiResponse: {
        //     "status": 200,
        //     "message": "Token created 1.",
        //     "cid": "PLOL",
        //     "token": "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJhdWQiOiJodHRwczovL2lkZW50aXR5dG9vbGtpdC5nb29nbGVhcGlzLmNvbS9nb29nbGUuaWRlbnRpdHkuaWRlbnRpdHl0b29sa2l0LnYxLklkZW50aXR5VG9vbGtpdCIsImlhdCI6MTYyMzY4NTgwNSwiZXhwIjoxNjIzNjg5NDA1LCJpc3MiOiJ0aGUtam91cm5leS1wZXJpbWV0ZXJAYXBwc3BvdC5nc2VydmljZWFjY291bnQuY29tIiwic3ViIjoidGhlLWpvdXJuZXktcGVyaW1ldGVyQGFwcHNwb3QuZ3NlcnZpY2VhY2NvdW50LmNvbSIsInVpZCI6InAyVTUxcXJaMlNObXBzM05SWHFiYmxId0dUNTIifQ.mBNsS_5tOVUz_I20LW2b8Z55qfeGP0I93x598_tudcaizMTeiQckt09Vx7PF3FtfhSVLJt0cHTaub8fzU0MWVY-0xKpGtH-TfQAU1NiVTL4MAY8CwgQIQLebBwJ-F4J0mRJwLps6OfaX-s5TI7v0syP49WgpPlZDGYO45gYFQEOlAReN12uG3oZS-gIpXFEufSck-8E1C8_yY2z59lesa7WFZO7OFTVrxV5nDb2dWKl-7025lrZqAzET5v5gjXojTOwfBodlio9l2cShCnde4h6lzk0i_m0oad4FZCpQS2u9r5NmVRos6KrTiymUAx8gPu1c-kdWz38rgJAJih6RCQ",
        // },
      }
    },
    components: {
      HowToUseThisPage,
      CodeSnippets,
      CodeIcon,
    },
    computed: {
      ...mapGetters('UserData', ['isAuthenticated']),
      churchid() {
        return this.$store.getters['AppData/cid']
      },
      handleDisabled() {
        // checks to disable the create key button
        return (
          (this.isAuthenticated &&
            !this.checkAuthorization(['superAdmin', 'admin'])) ||
          this.loading ||
          this.apiKey.length > 0 ||
          !this.churchid
        )
      },
      indexedItems() {
        return this.endpoints.map((item, index) => ({
          id: index,
          ...item,
        }))
      },
      indexedUserEndpoints() {
        return this.createUserEndpoints.map((item, index) => ({
          id: index,
          ...item,
        }))
      },
      limitedResponse() {
        if (!Array.isArray(this.response)) return this.response
        return this.showAllResults
          ? this.response
          : this.response.slice(0, this.responseLimit)
      },
      headers() {
        return [
          {
            title: this.$t('api.tableHeaders.key'),
            sortable: false,
            key: 'key',
          },
          {
            title: this.$t('api.tableHeaders.created'),
            sortable: false,
            key: 'created',
          },
          {
            title: this.$t('api.tableHeaders.revoke'),
            sortable: false,
            key: 'revoke',
            align: 'end',
          },
        ]
      },
      endpointHeaders() {
        return [
          {
            title: this.$t('api.tableHeaders.endpoint'),
            sortable: false,
            key: 'title',
            width: '15%',
          },
          {
            title: this.$t('api.tableHeaders.request'),
            sortable: false,
            key: 'request',
            width: '10%',
          },
          {
            title: this.$t('api.tableHeaders.location'),
            sortable: false,
            key: 'location',
            width: '40%',
          },
          {
            title: this.$t('api.tableHeaders.action'),
            sortable: false,
            key: 'test',
            width: '35%',
            align: 'end',
          },
        ]
      },
      sampleParamsHeaders() {
        return [
          {
            title: this.$t('api.tableHeaders.param'),
            sortable: false,
            key: 'param',
            width: '10%',
          },
          {
            title: this.$t('api.tableHeaders.type'),
            sortable: false,
            key: 'type',
            width: '10%',
          },
          {
            title: this.$t('api.tableHeaders.required'),
            sortable: false,
            key: 'required',
            width: '15%',
          },
          {
            title: this.$t('api.tableHeaders.value'),
            sortable: false,
            key: 'value',
            width: '20%',
          },
          {
            title: this.$t('api.tableHeaders.description'),
            sortable: false,
            key: 'description',
            width: '40%',
          },
        ]
      },
      tokenHeaders() {
        return [
          {
            title: this.$t('api.tableHeaders.param'),
            sortable: false,
            key: 'param',
            width: '15%',
          },
          {
            title: this.$t('api.tableHeaders.response'),
            sortable: false,
            key: 'value',
            width: '45%',
          },
          {
            title: this.$t('api.tableHeaders.description'),
            sortable: false,
            key: 'description',
            width: '40%',
          },
        ]
      },
      getTokenParams() {
        return [
          {
            param: 'email',
            required: this.$t('api.params.required.yes'),
            type: this.$t('api.params.type.string'),
            value: this.$t('api.params.value.email'),
            description: this.$t('api.params.description.email'),
          },
          {
            param: 'cid',
            required: this.$t('api.params.required.yes'),
            type: this.$t('api.params.type.string'),
            value: this.$t('api.params.value.cid'),
            description: this.$t('api.params.description.cid'),
          },
        ]
      },
      sampleParams() {
        return [
          {
            param: 'mode',
            required: this.$t('api.params.required.yes'),
            type: this.$t('api.params.type.string'),
            value: this.$t('api.params.value.mode'),
            description: this.$t('api.params.description.mode'),
          },
          {
            param: 'cid',
            required: this.$t('api.params.required.yes'),
            type: this.$t('api.params.type.string'),
            value: this.$t('api.params.value.cid'),
            description: this.$t('api.params.description.cid'),
          },
          {
            param: 'active',
            required: this.$t('api.params.required.optional'),
            type: this.$t('api.params.type.string'),
            value: this.$t('api.params.value.active'),
            description: this.$t('api.params.description.active'),
          },
          {
            param: 'token',
            required: this.$t('api.params.required.yes'),
            type: this.$t('api.params.type.string'),
            value: this.$t('api.params.value.token'),
            description: this.$t('api.params.description.token'),
          },
        ]
      },
      tokenSampleParams() {
        return [
          {
            param: 'status',
            value: '200',
            description:
              'Standard response for successful HTTP requests. Anything other than 200 contact tech support.',
          },
          {
            param: 'message',
            value: 'Token created 1 / Token created 2',
            description:
              '1 - the user already exists. 2 - a new user was created.',
          },
          {
            param: 'token',
            value:
              'eyJhbGciOiJSUzr1NiIsInR5cCI6IkpXVCJ9.GyJhdWQiOiJodHRwczovL2lkZW50aXR5dG9vbGtpdC5nb29nbGVhcGlzLmNvbS7nb29nb...',
            description:
              'Login token. Long string consisting of letters, numbers and special characters that will be used in the next step to log the user in.',
          },
        ]
      },
    },
    watch: {
      churchid: {
        deep: true,
        handler(event) {
          this.init(event)
        },
      },
      loader() {
        const l = this.loader
        this[l] = !this[l]
        this.loader = null
      },
    },
    methods: {
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const normalizedPermissions = permissions.includes('superAdmin')
            ? [...permissions, 'helpDesk']
            : permissions

          const hasRole = normalizedPermissions.filter(() =>
            userRoles.some((role) => normalizedPermissions.includes(role))
          )
          if (hasRole && hasRole.length > 0) return true
        }
        return false
      },
      isHelpDesk() {
        const userRoles = this.$store.getters['UserData/getRoles']
        return Array.isArray(userRoles) && userRoles.includes('helpDesk')
      },
      async deleteItem(item, collection) {
        if (this.isHelpDesk && this.isHelpDesk()) {
          const key = 'api.messages.deleteNotAllowed'
          this.feedback =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot revoke API keys.'
          return
        }

        if (item && collection) {
          const confirmed = await confirmAction({
            title: `${this.$t('api.revokeDialog.title')} <br>"${item.key}"`,
            html: this.$t('api.confirmRevoke'),
            input: 'text',
            inputPlaceholder: this.$t('api.revokePlaceholder'),
            inputValidator: (value) => {
              return new Promise((resolve) => {
                if (value === this.$t('api.revokePlaceholder')) {
                  resolve()
                } else {
                  resolve(this.$t('api.revokeValidation'))
                }
              })
            },
            confirmButtonText: this.$t('api.revokeDialog.confirmButton'),
            cancelButtonText: this.$t('api.revokeDialog.cancelButton'),
            buttonsStyling: false,
            customClass: {
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
            context: this, // pass this so we can detect theme
          })

          if (confirmed) {
            await db.collection(collection).doc(item.id).delete()
            this.init(this.$store.getters['AppData/cid'])
          }
        } else {
          Swal.fire({
            title: 'Houston we have a problem.',
            text: 'Error with the revoke function, missing params.',
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
        }
      },
      generateKey() {
        let d = new Date().getTime()

        if (
          window.performance &&
          typeof window.performance.now === 'function'
        ) {
          d += performance.now()
        }

        let uuid = 'xxxxxxxx-xxxx-yxxx-yxxx-xxxxxxxxxxxx'.replace(
          /[xy]/g,
          function (c) {
            var r = (d + Math.random() * 16) % 16 | 0
            d = Math.floor(d / 16)
            return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16)
          }
        )

        return uuid
      },
      async createNewApiKey() {
        // console.log('createNewApiKey called')
        this.loader = 'loading'

        let apiKey
        let newKEY
        do {
          apiKey = this.generateKey()
          let apiKeySnapshot = await db
            .collection('apikeys')
            .where('cid', '==', this.$store.getters['AppData/cid'])
            .where('key', '==', apiKey)
            .get()
          if (apiKeySnapshot.empty) {
            newKEY = apiKey
          }
        } while (!newKEY)

        // console.log('newKEY ', newKEY)

        if (newKEY) {
          await db.collection('apikeys').add({
            cid: this.$store.getters['AppData/cid'],
            key: newKEY,
            created: new Date().toISOString().substr(0, 10),
          })

          this.snackbar = true
          this.snackbarMsg = 'Your new API key has been created'
          this.loader = null
          this.loading = false

          this.init(this.$store.getters['AppData/cid'])
        }
      },
      getInstructions() {
        this.loading3 = true
        this.response = ''

        // small delay for user experience
        setTimeout(() => {
          this.loader = null
          this.loading3 = false
          this.snackbar = true
          this.snackbarMsg = 'Your API instructions are below'
          this.createUserResponse = true
          this.code = false
        }, 1000)
      },
      testEndPoint(item) {
        this.loading2 = true
        this.response = ''
        this.code = false
        this.showAllResults = false

        let endPoint

        if (item.endpoint === 'user' && !this.id?.trim()) {
          this.snackbar = true
          this.snackbarMsg = 'You need to enter a user ID'
          this.loading2 = false
          return
        }

        if (!this.apiKey.length || !this.apiKey[0]?.key) {
          this.snackbar = true
          this.snackbarMsg = 'No API key available.'
          this.loading2 = false
          return
        }

        switch (item.endpoint) {
          case 'users':
            endPoint = functionUrl('users')
            break
          case 'groups':
            endPoint = functionUrl('groups')
            break
          case 'user':
            endPoint = functionUrl('user/' + this.id)
            break
        }

        setTimeout(() => {
          const config = {
            method: 'get',
            url: endPoint,
            params: {
              authkey: this.apiKey[0].key,
            },
          }

          axios(config)
            .then((response) => {
              const data = response.data

              // ✅ Normalize and de-proxy response
              this.response = JSON.parse(
                JSON.stringify(toRaw(data?.users || data?.groups || data))
              )

              this.snackbar = true
              this.snackbarMsg = 'Your API results are below'
            })
            .catch((error) => {
              console.error('ERROR:', error)
              this.snackbar = true
              this.snackbarMsg = 'Error contacting the API.'
            })
            .finally(() => {
              this.loader = null
              this.loading2 = false
            })
        }, 1000)
      },
      showCode(item) {
        if (
          item.endpoint === 'user' &&
          (this.id == '' || this.id == null || this.id == undefined)
        ) {
          this.snackbar = true
          this.snackbarMsg = 'You need to enter a user ID'
          this.loader = null
          this.loading2 = false
          return
        }

        if (this.id !== '' && this.id !== null && this.id !== undefined)
          this.id = this.id.replace(/\s/g, '')

        if (item.endpoint === 'user') {
          this.location = item.location.replace('{id}', this.id)
        } else [(this.location = item.location)]

        this.componentKey += 1
        this.response = ''
        this.request = 'GET'
        this.snackbar = true
        this.snackbarMsg = 'Your code snippets are below'

        this.auth = this.apiKey[0].key
        this.code = true

        // console.log(this.backdoor)
      },
      showUserCode(item) {
        if (this.id !== '' && this.id !== null && this.id !== undefined)
          this.id = this.id.replace(/\s/g, '')

        this.location = item.location
        this.componentKey += 1
        this.response = ''
        this.createUserResponse = ''
        this.request = 'POST'
        this.snackbar = true
        this.snackbarMsg = 'Your code snippets are below'

        this.auth = this.apiKey[0].key
        this.code = true
      },
      downloadResponseAsJson() {
        const blob = new Blob([JSON.stringify(this.response, null, 2)], {
          type: 'application/json',
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'api-response.json'
        a.click()
        URL.revokeObjectURL(url)
      },
      init(cid) {
        this.apiKey = []
        this.isloading = true

        let apiRef = db.collection('apikeys')
        apiRef = apiRef.where('cid', '==', cid)
        apiRef
          .get()
          .then((snapshot) => {
            snapshot.forEach((doc) => {
              let user = doc.data()
              user.id = doc.id
              this.apiKey.push(user)
            })
          })
          .then(() => {
            this.isloading = false
          })
      },
    },
    created() {
      this.init(this.$store.getters['AppData/cid'])
    },
  }
</script>

<style scoped>
  .apikey-container {
    margin-top: 50px;
    margin-bottom: 25%;
  }
  .apikey-container .apikey-icon {
    margin-right: 10px;
  }
  .show-response {
    max-height: 800px;
    overflow: scroll;
  }
  .show-sample-response {
    overflow-wrap: anywhere;
    padding: 30px;
  }
</style>
